import { useState, useEffect } from 'react';
import { updateMetaTags } from '../../utils/seo';

export default function ConfidentialitePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    updateMetaTags({
      title: 'Politique de confidentialité - Vist Magazine',
      description: 'Politique de confidentialité de Vist Magazine. Découvrez comment nous collectons, utilisons et protégeons vos données personnelles.',
      keywords: 'confidentialité, données personnelles, rgpd, vist magazine, protection des données',
      canonical: '/confidentialite'
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="text-xl md:text-2xl font-bold text-gray-900">Vist</a>
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Accueil</a>
              <a href="/magazine" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Magazine</a>
              <a href="/blog" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Blog</a>
              <a href="/a-propos" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">À propos</a>
              <a href="/contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl text-gray-900`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <a href="/" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Accueil</a>
              <a href="/magazine" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Magazine</a>
              <a href="/blog" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Blog</a>
              <a href="/a-propos" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">À propos</a>
              <a href="/contact" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Contact</a>
            </div>
          </div>
        )}
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-20">
        <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-8 md:mb-12">Politique de confidentialité</h1>

        <div className="prose prose-sm md:prose-base max-w-none space-y-8">
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Vist Magazine accorde une grande importance à la protection de vos données personnelles. Cette politique de confidentialité vous informe sur la manière dont nous collectons, utilisons et protégeons vos informations.
            </p>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              En utilisant notre site, vous acceptez les pratiques décrites dans cette politique.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">2. Données collectées</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Nous collectons les types de données suivants :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm md:text-base text-gray-700">
              <li><strong>Données d'identification :</strong> nom, prénom, adresse e-mail</li>
              <li><strong>Données de navigation :</strong> adresse IP, type de navigateur, pages visitées, durée de visite</li>
              <li><strong>Données de communication :</strong> messages envoyés via le formulaire de contact</li>
              <li><strong>Cookies :</strong> informations stockées sur votre appareil pour améliorer votre expérience</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">3. Finalités du traitement</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Vos données sont collectées pour les finalités suivantes :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm md:text-base text-gray-700">
              <li>Répondre à vos demandes de contact</li>
              <li>Vous envoyer notre newsletter (avec votre consentement)</li>
              <li>Améliorer nos services et notre contenu</li>
              <li>Analyser le trafic et l'utilisation du site</li>
              <li>Assurer la sécurité et prévenir la fraude</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">4. Base légale du traitement</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Le traitement de vos données repose sur :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm md:text-base text-gray-700">
              <li><strong>Votre consentement :</strong> pour l'envoi de la newsletter et l'utilisation de cookies</li>
              <li><strong>L'exécution d'un contrat :</strong> pour répondre à vos demandes</li>
              <li><strong>Notre intérêt légitime :</strong> pour améliorer nos services et assurer la sécurité du site</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">5. Destinataires des données</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Vos données peuvent être transmises à :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm md:text-base text-gray-700">
              <li>Notre équipe interne pour la gestion du site</li>
              <li>Nos prestataires techniques (hébergement, analytics)</li>
              <li>Les autorités compétentes en cas d'obligation légale</li>
            </ul>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mt-3">
              Nous ne vendons ni ne louons vos données personnelles à des tiers.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">6. Durée de conservation</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Vos données sont conservées pendant les durées suivantes :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm md:text-base text-gray-700">
              <li><strong>Données de contact :</strong> 3 ans à compter du dernier contact</li>
              <li><strong>Newsletter :</strong> jusqu'à votre désinscription</li>
              <li><strong>Données de navigation :</strong> 13 mois maximum</li>
              <li><strong>Cookies :</strong> 13 mois maximum</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">7. Vos droits</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Conformément au RGPD, vous disposez des droits suivants :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm md:text-base text-gray-700">
              <li><strong>Droit d'accès :</strong> obtenir une copie de vos données</li>
              <li><strong>Droit de rectification :</strong> corriger vos données inexactes</li>
              <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données</li>
              <li><strong>Droit à la limitation :</strong> limiter le traitement de vos données</li>
              <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
              <li><strong>Droit à la portabilité :</strong> récupérer vos données dans un format structuré</li>
              <li><strong>Droit de retirer votre consentement :</strong> à tout moment</li>
            </ul>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mt-3">
              Pour exercer ces droits, contactez-nous via notre <a href="/contact" className="text-amber-600 hover:text-amber-700 underline cursor-pointer">page de contact</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">8. Cookies</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Nous utilisons des cookies pour :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm md:text-base text-gray-700">
              <li>Assurer le bon fonctionnement du site</li>
              <li>Mémoriser vos préférences</li>
              <li>Analyser le trafic et améliorer nos services</li>
            </ul>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mt-3">
              Vous pouvez désactiver les cookies dans les paramètres de votre navigateur, mais cela peut affecter certaines fonctionnalités du site.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">9. Sécurité</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre tout accès non autorisé, perte, destruction ou altération. Toutefois, aucune transmission de données sur Internet n'est totalement sécurisée.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">10. Modifications</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. Les modifications seront publiées sur cette page avec une nouvelle date de mise à jour.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">11. Contact</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              Pour toute question concernant cette politique de confidentialité ou l'exercice de vos droits, contactez-nous via notre <a href="/contact" className="text-amber-600 hover:text-amber-700 underline cursor-pointer">page de contact</a>.
            </p>
          </section>

          <section className="pt-6 border-t border-gray-200">
            <p className="text-xs md:text-sm text-gray-500">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50 py-12 md:py-16 mt-12 md:mt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-12 mb-8 md:mb-12">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Vist</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Magazine digital indépendant consacré à l'analyse des tendances culturelles, sociétales et digitales.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-4">Navigation</h4>
              <ul className="space-y-2">
                <li><a href="/" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Accueil</a></li>
                <li><a href="/magazine" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Magazine</a></li>
                <li><a href="/blog" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Blog</a></li>
                <li><a href="/a-propos" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">À propos</a></li>
                <li><a href="/contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-4">Légal</h4>
              <ul className="space-y-2">
                <li><a href="/mentions-legales" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Mentions légales</a></li>
                <li><a href="/confidentialite" className="text-sm text-gray-900 font-medium cursor-pointer">Confidentialité</a></li>
                <li><a href="/conditions" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Conditions</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-6 md:pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center">
              © {new Date().getFullYear()} Vist Magazine. Tous droits réservés. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900 transition-colors">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
